import { useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Upload, Loader2, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inputCls = "rounded-xl bg-surface glow-border px-3 py-2 text-sm outline-none w-full";

export function parseRows(text) {
  return text.split(/\r?\n/).map((l) => l.replace(/^\s*(\d+[.)]|[-*•])\s*/, "").trim()).filter(Boolean).map((l) => {
    const [name, email] = l.split(/[,;\t]/).map((x) => x.trim());
    return { name, email: email && email.includes("@") ? email : null };
  }).filter((r) => r.name);
}

function sheetRows(file) {
  return file.arrayBuffer().then((buf) => {
    const wb = XLSX.read(buf);
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" });
    const head = (rows[0] || []).map((h) => String(h).toLowerCase());
    const ni = head.findIndex((h) => h.includes("name"));
    const ei = head.findIndex((h) => h.includes("mail"));
    const body = ni >= 0 ? rows.slice(1) : rows;
    return body.map((r) => ({ name: String(r[ni >= 0 ? ni : 0] || "").trim(), email: String(r[ei >= 0 ? ei : 1] || "").trim() || null }))
      .filter((r) => r.name).map((r) => ({ ...r, email: r.email && r.email.includes("@") ? r.email : null }));
  });
}

export function CreateExternalEvent({ onCreated }) {
  const [f, setF] = useState({ name: "", date: "", venue: "", category: "", platform: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const create = async () => {
    setBusy(true);
    try { await api.post("/ratings/external-events", f); toast.success("External event created"); setF({ name: "", date: "", venue: "", category: "", platform: "" }); onCreated(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="external-event-form">
      <input data-testid="xev-name" value={f.name} onChange={set("name")} placeholder="Event name" className={inputCls} style={{ color: "var(--text)" }} />
      <input data-testid="xev-date" type="date" value={f.date} onChange={set("date")} className={inputCls} style={{ color: "var(--text)" }} />
      <input data-testid="xev-venue" value={f.venue} onChange={set("venue")} placeholder="Venue" className={inputCls} style={{ color: "var(--text)" }} />
      <input data-testid="xev-category" value={f.category} onChange={set("category")} placeholder="Category / division" className={inputCls} style={{ color: "var(--text)" }} />
      <input data-testid="xev-platform" value={f.platform} onChange={set("platform")} placeholder="Software (e.g. PickleballBrackets)" className={inputCls} style={{ color: "var(--text)" }} />
      <button data-testid="xev-create-btn" disabled={busy || !f.name || !f.date} onClick={create} className="btn-glow rounded-full px-5 py-2 text-sm font-bold flex items-center justify-center gap-2 sm:col-span-5 sm:justify-self-start disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create external event
      </button>
    </div>
  );
}

export function ImportParticipants({ eventId, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (rows) => {
    if (!rows.length) { toast.error("No participants found"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/ratings/external-events/${eventId}/participants`, { rows });
      toast.success(`Added ${data.added} participants · ${data.matched} matched to profiles`);
      setText(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { await send(await sheetRows(file)); } catch { toast.error("Could not read that file"); }
  };
  return (
    <div className="space-y-2" data-testid={`import-${eventId}`}>
      <textarea data-testid={`import-text-${eventId}`} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={"One per line: Name, email (email optional)\nJuan Dela Cruz, juan@email.com"} className={inputCls} style={{ color: "var(--text)" }} />
      <div className="flex gap-2 flex-wrap">
        <button data-testid={`import-add-btn-${eventId}`} disabled={busy || !text.trim()} onClick={() => send(parseRows(text))} className="rounded-full glass px-4 py-2 text-xs font-bold disabled:opacity-40">Add participants</button>
        <label className="rounded-full glass px-4 py-2 text-xs font-bold flex items-center gap-1.5 cursor-pointer" data-testid={`import-file-label-${eventId}`}>
          <Upload className="w-3.5 h-3.5 text-primary-c" /> Import CSV / Excel
          <input data-testid={`import-file-${eventId}`} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} />
        </label>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-primary-c self-center" />}
      </div>
    </div>
  );
}
